import { useEffect, useMemo, useState } from 'react';

import { ViewProps } from './discussion.constants';
import {
  ACTIVITIES_ANSWER_TEXT,
  ACTIVITIES_RESPONSE_VARIANT_TEXT,
  ACTIVITIES_RESPONSE_VARIANT_DRAW,
  ACTIVITIES_ANSWER_DRAW_TYPE,
  LANGUAGE_CODE_EN_AU,
} from 'global/constants';
import { Box } from 'approot/shared/design-system/box/box.style';
import { useBatchAnswersContext } from '../shared/batch-answers.context';
import DiscussionTextResponse from './discussion-text-response';
import { useResponseBaseHeight } from '../response/response.hooks';
import {
  useActivityAnswerId,
  useTeacherGetAnswers,
} from 'approot/activity-builder/present/activity-answers.hooks';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import DrawCanvas from '../draw/canvas/draw-canvas';
import IconInformation from 'approot/shared/icons/icon-information';
import DiscussionViewQuestion from './discussion-view-question';
import { DISCUSSION_DRAW_CANVAS } from './discussion-view.constants';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import {
  DiscussionWrapper,
  DiscussionDrawWrapper,
  DiscussionShowInstruction,
  DiscussionWrapperInner,
} from './discussion-view-answer.style';
import { DiscussionQuestionOverride } from './discussion-view-question.style';
import DiscussionAnswerLink from './discussion-answer-link';

type Props = ViewProps & {
  isPublished: boolean;
};

const DiscussionViewAnswer = (props: Props) => {
  const { id, type, properties, subActivityProperties, isPublished } = props;

  const [selectedAnswerType, setSelectedAnswerType] = useState<string>();
  const [isRespondVisible, setIsRespondVisible] = useState<boolean>(false);
  const [isInstructionVisible, setIsInstructionVisible] =
    useState<boolean>(true);

  const canRespond = false;
  const baseHeight = useResponseBaseHeight();

  const isTextSelected =
    selectedAnswerType === ACTIVITIES_RESPONSE_VARIANT_TEXT;

  const { componentAnswers, variables } = useTeacherGetAnswers({
    fetchPolicy: 'cache-only',
    componentId: id,
    skip: !canRespond,
  });
  const { handleUpdate } = useBatchAnswersContext();

  const textAnswer = useMemo(
    () => componentAnswers?.find(c => c.answerType === ACTIVITIES_ANSWER_TEXT),
    [componentAnswers]
  );

  const drawAnswer = useMemo(
    () =>
      componentAnswers?.find(c => c.answerType === ACTIVITIES_ANSWER_DRAW_TYPE),
    [componentAnswers]
  );

  const textAnswerUuid = useActivityAnswerId(textAnswer);
  const drawAnswerUuid = useActivityAnswerId(drawAnswer);

  const answerOptions = useMemo(() => {
    const options = [];
    if (
      properties.discussion.responseTypes.includes(
        ACTIVITIES_RESPONSE_VARIANT_TEXT
      )
    ) {
      options.push({
        label: 'Text Response',
        value: ACTIVITIES_RESPONSE_VARIANT_TEXT,
      });
    }
    if (
      properties.discussion.responseTypes.includes(
        ACTIVITIES_RESPONSE_VARIANT_DRAW
      )
    ) {
      options.push({
        label: 'Drawing',
        value: ACTIVITIES_RESPONSE_VARIANT_DRAW,
      });
    }

    return options;
  }, [properties]);

  useEffect(() => {
    setSelectedAnswerType(answerOptions[0].value);
  }, [answerOptions]);

  return (
    <>
      {isInstructionVisible ? (
        <DiscussionWrapper>
          <DiscussionWrapperInner>
            <DiscussionQuestionOverride>
              <DiscussionViewQuestion
                id={id}
                type={type}
                discussion={properties.discussion}
                style={properties.style}
                isZoomed
                extra={
                  <>
                    {isPublished && canRespond && (
                      <DiscussionAnswerLink
                        isExpanded={isRespondVisible}
                        onClick={() => {
                          setIsRespondVisible(!isRespondVisible);
                        }}
                      />
                    )}

                    {isRespondVisible && (
                      <Box $alignItems="center" $gap={12}>
                        <CheckableSingle
                          isSmall
                          options={answerOptions}
                          selected={
                            answerOptions.find(
                              a => a.value === selectedAnswerType
                            )?.value || answerOptions[0].value
                          }
                          name="responseType"
                          onSelect={value => setSelectedAnswerType(value)}
                        />
                        {!isTextSelected && (
                          <LinkButton
                            type="button"
                            onClick={() =>
                              setIsInstructionVisible(!isInstructionVisible)
                            }
                          >
                            {isInstructionVisible
                              ? 'Hide Instruction'
                              : 'Show Instruction'}
                          </LinkButton>
                        )}
                      </Box>
                    )}
                  </>
                }
              />
            </DiscussionQuestionOverride>
          </DiscussionWrapperInner>
        </DiscussionWrapper>
      ) : (
        <DiscussionShowInstruction>
          <Box $gap={8} $alignItems="center">
            <IconInformation width={22} height={22} />
            <LinkButton
              type="button"
              onClick={() => setIsInstructionVisible(true)}
            >
              Show Instruction
            </LinkButton>
          </Box>
        </DiscussionShowInstruction>
      )}

      {isRespondVisible && isTextSelected && (
        <DiscussionWrapper>
          <DiscussionTextResponse
            answer={textAnswer}
            baseHeight={baseHeight}
            onChange={json => {
              if (variables && subActivityProperties?.activityComponentId) {
                handleUpdate({
                  input: {
                    __typename: 'ActivityAnswer',
                    uuid: textAnswerUuid,
                    activityId: subActivityProperties.activityComponentId,
                    componentId: id,
                    answerType: ACTIVITIES_ANSWER_TEXT,
                    createdAt: null,
                    updatedAt: new Date(),
                    studentId: null,
                    answer: {
                      __typename: 'RichtextAnswer',
                      answerType: ACTIVITIES_ANSWER_TEXT,
                      richtext: json,
                    },
                    answerReferences: null,
                  },
                  variables,
                });
              }
            }}
          />
        </DiscussionWrapper>
      )}

      {isRespondVisible && !isTextSelected && (
        <DiscussionDrawWrapper>
          <DrawCanvas
            id={id}
            type={type}
            language={LANGUAGE_CODE_EN_AU}
            dimensionsData={DISCUSSION_DRAW_CANVAS}
            answer={(drawAnswer?.answer as DrawAnswerFragment)?.drawing}
            onSave={content => {
              if (
                canRespond &&
                subActivityProperties?.activityComponentId &&
                variables
              ) {
                handleUpdate({
                  variables,
                  input: {
                    __typename: 'ActivityAnswer',
                    uuid: drawAnswerUuid,
                    answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
                    activityId: subActivityProperties.activityComponentId,
                    componentId: props.id,
                    createdAt: null,
                    updatedAt: new Date(),
                    studentId: null,
                    answer: {
                      __typename: 'DrawAnswer',
                      answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
                      drawing: content,
                      hasDrawingData: true,
                    },
                    answerReferences: null,
                  },
                });
              }
            }}
          />
        </DiscussionDrawWrapper>
      )}
    </>
  );
};

export default DiscussionViewAnswer;
